import React, { useState, useMemo } from 'react'
import { CloseIcon } from './Icons'
import SearchableSelect from './SearchableSelect'

const ControllerModal = ({
  initial,
  assets = '',
  tenantPublicId,
  onSave,
  onClose,
  isSaving
}) => {
  const blank = {
    name: '',
    broker_host: '',
    broker_port: 1883,
    asset_id: ''
  }

  const [form, setForm] = useState(
    initial
      ? {
          name: initial.name || '',
          broker_host: initial.broker_host || '',
          broker_port: initial.broker_port ?? 1883,
          asset_id: initial.asset_ids?.[0] || ''
        }
      : blank
  )

  const [copiedKey, setCopiedKey] = useState(null)

  const safeAssets = Array.isArray(assets) ? assets : assets?.data || []

  // Derive topic preview from selected asset (location comes from asset).
  // The device id belongs to the controller — known only after it is saved.
  const topicPreview = useMemo(() => {
    const asset = safeAssets.find(a => a.id === form.asset_id)
    const locId = asset?.location?.id || asset?.location_id
    if (!locId) return null

    // client_id: from asset (preferred) → JWT context → derived from tenant_id
    const clientId =
      asset.client_id ||
      tenantPublicId ||
      (asset.tenant_id ? `1${String(asset.tenant_id).padStart(11, '0')}` : null)
    if (!clientId) return null

    // Location public_id: use stored value or derive (2 + 8-digit pad)
    const locationId =
      asset.location?.public_id || `2${String(locId).padStart(8, '0')}`

    const assetName = asset.name
    const deviceId = initial?.device_id || '⟨device-id⟩'

    return `${clientId}/${locationId}/${assetName}/${deviceId}`
  }, [form.asset_id, safeAssets, tenantPublicId, initial])

  const selectedAsset = safeAssets.find(a => a.id === form.asset_id)

  const copyToClipboard = (key, value) => {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 1500)
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    onSave({
      name: form.name,
      broker_host: form.broker_host || null,
      broker_port: form.broker_port ? Number(form.broker_port) : 1883,
      asset_ids: form.asset_id ? [form.asset_id] : [],
      topic: null // always auto-generated by backend
    })
  }

  const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'>
      <div className='bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0'>
          <h3 className='text-base font-bold text-gray-800'>
            {initial ? 'Edit Controller' : 'Add New Controller'}
          </h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600'
          >
            <CloseIcon />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className='flex flex-col flex-1 overflow-hidden'
        >
          <div className='p-6 space-y-4 overflow-y-auto flex-1'>
            {/* Controller Name */}
            <div>
              <label className='block text-xs font-semibold text-gray-700 mb-1'>
                Controller Name *
              </label>
              <input
                required
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. Gateway Alpha'
                className={inputCls}
              />
            </div>

            {/* Broker Host + Port */}
            <div className='grid grid-cols-2 gap-3'>
              <div className='col-span-2 sm:col-span-1'>
                <label className='block text-xs font-semibold text-gray-700 mb-1'>
                  Broker Host
                </label>
                <input
                  value={form.broker_host}
                  onChange={e =>
                    setForm({ ...form, broker_host: e.target.value })
                  }
                  placeholder='e.g. mqtt.example.com'
                  className={inputCls}
                />
              </div>
              <div>
                <label className='block text-xs font-semibold text-gray-700 mb-1'>
                  Broker Port
                </label>
                <input
                  type='number'
                  min='1'
                  max='65535'
                  value={form.broker_port}
                  onChange={e =>
                    setForm({
                      ...form,
                      broker_port:
                        e.target.value === ''
                          ? ''
                          : parseInt(e.target.value, 10)
                    })
                  }
                  placeholder='1883'
                  className={inputCls}
                />
              </div>
            </div>

            {/* Asset — only selection needed; location derived automatically */}
            <div>
              <label className='block text-xs font-semibold text-gray-700 mb-1'>
                Asset *
                <span className='ml-1 font-normal text-gray-400 text-[10px]'>
                  Location is auto-derived from asset
                </span>
              </label>
              {safeAssets.length === 0 ? (
                <div className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400 text-center'>
                  No assets found. Add assets in Asset Management first.
                </div>
              ) : (
                <SearchableSelect
                  options={safeAssets}
                  value={form.asset_id}
                  onChange={id => setForm({ ...form, asset_id: id })}
                  displayKey='name'
                />
              )}
            </div>

            {/* Selected asset summary */}
            {selectedAsset && (
              <div className='rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 space-y-1.5 text-xs'>
                {[
                  { label: 'Device ID', value: initial?.device_id || 'auto-generated on save' },
                  {
                    label: 'Location',
                    value:
                      selectedAsset.location?.name ||
                      (selectedAsset.location_id
                        ? `ID: ${selectedAsset.location_id}`
                        : null)
                  },
                  { label: 'Type', value: selectedAsset.asset_type }
                ].map(({ label, value }) =>
                  value ? (
                    <div key={label} className='flex items-center gap-2'>
                      <span className='text-gray-400 w-20 flex-shrink-0'>
                        {label}
                      </span>
                      <span className='font-mono text-gray-700'>{value}</span>
                    </div>
                  ) : null
                )}
              </div>
            )}

            {/* MQTT Topic preview */}
            <div>
              <label className='block text-xs font-semibold text-gray-700 mb-1'>
                MQTT Topic
              </label>
              <div className='px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg'>
                {topicPreview ? (
                  <div className='flex items-center justify-between gap-2'>
                    <span className='font-mono text-xs text-gray-700 break-all leading-relaxed'>
                      {topicPreview}
                    </span>
                    <button
                      type='button'
                      onClick={() => copyToClipboard('topic', topicPreview)}
                      className='text-[10px] text-blue-600 hover:text-blue-800 flex-shrink-0 font-medium'
                    >
                      {copiedKey === 'topic' ? 'Copied ✓' : 'Copy'}
                    </button>
                  </div>
                ) : (
                  <span className='text-xs text-gray-400 italic'>
                    {!form.asset_id
                      ? 'Select an asset to preview topic'
                      : 'Asset has no location — assign a location to the asset first'}
                  </span>
                )}
              </div>
              <p className='text-[10px] text-gray-400 mt-1'>
                Format: clientId / locationId / assetName / deviceId
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className='flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0'>
            <button
              type='button'
              onClick={onClose}
              className='flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSaving}
              className='flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded-lg hover:bg-blue-900 font-medium disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {isSaving
                ? 'Saving…'
                : initial
                ? 'Save Changes'
                : 'Add Controller'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ControllerModal
